import { jsx as _jsx } from "react/jsx-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { CreateConsentModal } from 'libs.consent.react';
import { useFeatureFlags } from 'libs.flags.react';
import { buildDocumentCreateUrl } from './participant_documents.utils';
import { SITE_FEATURE_FLAGS } from '../../constants/feature_flags.constants';
import { getApiErrorMessage, getApiInstance } from '../../helpers/api.helpers';
import { appendSiteIdToPath } from '../../helpers/links.helpers';
import { useAxonAccountContext } from '../../hooks/use_axon_account_context';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { useSiteContext } from '../../hooks/use_site_context';
export const useCreateConsentAction = (participant, canCreateDocument) => {
    const { environment } = useCurrentEnvironment();
    const site = useSiteContext();
    const account = useAxonAccountContext();
    const queryClient = useQueryClient();
    const { isFeatureEnabled } = useFeatureFlags();
    const [isCreateConsentModalOpen, setIsCreateConsentModalOpen] = useState(false);
    const getCreateConsentApiInstance = useCallback((currentEnvironment) => getApiInstance(currentEnvironment), []);
    const openClassicCreate = () => {
        const createDocumentUrl = buildDocumentCreateUrl(participant?._id, environment);
        if (createDocumentUrl && site?._id) {
            window.open(appendSiteIdToPath(createDocumentUrl, site._id), '_blank');
        }
    };
    const invalidateParticipantDocuments = () => {
        if (participant?._id) {
            void queryClient.invalidateQueries({ queryKey: ['participantDocuments', participant._id] });
        }
    };
    const isDocGenEconsentEnabled = isFeatureEnabled(SITE_FEATURE_FLAGS.DOC_GEN_ECONSENT);
    const handleCreateConsent = () => {
        if (isDocGenEconsentEnabled) {
            setIsCreateConsentModalOpen(true);
            return;
        }
        openClassicCreate();
    };
    // Portal the modal to document.body so it escapes the HeadlessUI TabPanel.
    // ModalWindow uses HeadlessUI Dialog which conflicts with the parent
    // TabPanel's focus/state management when rendered inline.
    const createConsentModal = canCreateDocument && isDocGenEconsentEnabled && isCreateConsentModalOpen && participant
        ? createPortal(_jsx(CreateConsentModal, { isOpen: true, onClose: () => setIsCreateConsentModalOpen(false), participant: participant, environment: environment, siteId: site?._id, accountId: account?._id, getApiInstance: getCreateConsentApiInstance, getApiErrorMessage: getApiErrorMessage, onInviteSuccess: invalidateParticipantDocuments }), document.body)
        : null;
    return { handleCreateConsent, createConsentModal };
};
